import {
  MedicalTreatmentBillIcon,
  MedicalTreatmentRecordIcon,
} from "@/lib/utils/iconUtils";

import { TooltipWrapper } from "@/components/ui/shadcn/Tooltip/TooltipWrapper";
import type { TreatmentViewModel } from "@/hooks/matter-view-models/use-medical-treatment-view-model";
import { cn } from "@/lib/utils/cn";
import pluralize from "pluralize";

export function getMedicalTreatmentRecordStatusLabel(
  status?: string | null,
  _hasReference?: boolean,
) {
  void _hasReference;
  if (status === "N/A") {
    return "-";
  }
  return status || "-";
}

export function getMedicalTreatmentRecordStatusClassName(
  status?: string | null,
  _hasReference?: boolean,
) {
  void _hasReference;
  return cn(
    status === "Received" && "text-green-600",
    status === "Requested" && "text-amber-600",
    (status === "Rejected" || status === "Not yet requested") && "text-red-600",
    (!status || status === "N/A") && "text-muted-foreground",
  );
}

export function AppointmentRecordStatusIcon({
  appointment,
  type = "medical",
  notApplicable,
}: {
  appointment: Pick<
    TreatmentViewModel["appointments"][number],
    | "records"
    | "bills"
    | "receivedRecordsCount"
    | "receivedBillsCount"
    | "allRecordsReceived"
    | "allBillsReceived"
    | "recordsNotApplicable"
    | "billsNotApplicable"
  >;
  type?: "medical" | "billing";
  checkIfConnectionsAreComplete?: boolean;
  notApplicable?: boolean;
}) {
  const items = type === "medical" ? appointment.records : appointment.bills;
  const receivedCount =
    type === "medical"
      ? appointment.receivedRecordsCount
      : appointment.receivedBillsCount;
  const allReceived =
    type === "medical"
      ? appointment.allRecordsReceived
      : appointment.allBillsReceived;
  const Icon =
    type === "medical" ? MedicalTreatmentRecordIcon : MedicalTreatmentBillIcon;
  const referenceCount = items.filter((item) => item.hasReference).length;
  const isNotApplicable =
    notApplicable ??
    (type === "medical"
      ? appointment.recordsNotApplicable
      : appointment.billsNotApplicable);
  const tooltipMessage = isNotApplicable
    ? "Not applicable"
    : items.length > 0
      ? `${receivedCount} of ${items.length} ${type} records received${
          referenceCount < items.length
            ? `; missing ${pluralize(
                "file reference",
                items.length - referenceCount,
                true,
              )}`
            : ` and all records have file references`
        }`
      : `No ${type} records requested`;
  return (
    <TooltipWrapper message={tooltipMessage}>
      <div>
        <Icon
          className={cn(
            "h-4 w-4",
            isNotApplicable ? "text-muted-foreground" : "text-red-500",
            !isNotApplicable &&
              items.length > 0 &&
              (receivedCount < items.length || referenceCount < items.length) &&
              "text-yellow-500",
            !isNotApplicable &&
              items.length > 0 &&
              allReceived &&
              referenceCount === items.length &&
              "text-green-500",
          )}
        />
      </div>
    </TooltipWrapper>
  );
}

export function TreatmentRecordStatusIcon({
  treatment,
  type = "medical",
}: {
  treatment: Pick<
    TreatmentViewModel,
    | "appointments"
    | "records"
    | "bills"
    | "everyAppointmentHasMedicalConnections"
    | "missingAppointmentMedicalConnectionsCount"
    | "everyAppointmentHasBillingConnections"
    | "missingAppointmentBillingConnectionsCount"
    | "allRecordsHaveReferences"
    | "allBillsHaveReferences"
    | "recordsWithReferencesCount"
    | "billsWithReferencesCount"
    | "recordNotApplicable"
    | "billNotApplicable"
    | "planNotApplicable"
  >;
  type?: "medical" | "billing";
}) {
  const records = treatment.records;
  const bills = treatment.bills;
  const appointments = treatment.appointments;
  const items = type === "medical" ? records : bills;
  const itemStatusKey = type === "medical" ? "status" : "recordStatus";
  const receivedCount = items.filter(
    (item) => item[itemStatusKey as keyof typeof item] === "Received",
  ).length;
  const allReceived = receivedCount === items.length;
  const everyAppointmentHasConnections =
    type === "medical"
      ? treatment.everyAppointmentHasMedicalConnections
      : treatment.everyAppointmentHasBillingConnections;
  const missingAppointmentConnectionsCount =
    type === "medical"
      ? treatment.missingAppointmentMedicalConnectionsCount
      : treatment.missingAppointmentBillingConnectionsCount;
  const totalCount = items.length;
  const referenceCount =
    type === "medical"
      ? treatment.recordsWithReferencesCount
      : treatment.billsWithReferencesCount;
  const allReferences =
    type === "medical"
      ? treatment.allRecordsHaveReferences
      : treatment.allBillsHaveReferences;

  const receivedMessage = `${receivedCount} of ${totalCount} ${type} records received`; // "4 of 10 records received"
  const referenceMessage =
    totalCount === 0
      ? ""
      : referenceCount < totalCount
        ? `; missing ${pluralize("file reference", totalCount - referenceCount, true)}`
        : ` and all ${type === "medical" ? "records" : "bills"} have file references`;
  const appointmentsMessage =
    appointments.length > 0
      ? ` and all appointments (${appointments.length}) have connections`
      : `; no appointments have been added`; // "and all 4 appointments have connections" OR ";no appointments have been added"

  let message = `${receivedMessage}${referenceMessage}`;
  if (everyAppointmentHasConnections) {
    message += appointmentsMessage;
  } else {
    message += `; missing connections for ${pluralize("appointment", missingAppointmentConnectionsCount, true)}`;
  }

  const Icon =
    type === "medical" ? MedicalTreatmentRecordIcon : MedicalTreatmentBillIcon;

  const appointmentStatuses = appointments.map((appointment) => {
    const appointmentItems =
      type === "medical" ? appointment.records : appointment.bills;
    const hasItems = appointmentItems.length > 0;
    const appointmentReceivedCount =
      type === "medical"
        ? appointment.receivedRecordsCount
        : appointment.receivedBillsCount;
    const appointmentAllReceived =
      type === "medical"
        ? appointment.allRecordsReceived
        : appointment.allBillsReceived;
    const appointmentReferenceCount = appointmentItems.filter(
      (item) => item.hasReference,
    ).length;
    const appointmentGreen =
      hasItems &&
      appointmentAllReceived &&
      appointmentReferenceCount === appointmentItems.length;
    const appointmentYellow =
      hasItems &&
      !appointmentGreen &&
      (appointmentReceivedCount < appointmentItems.length ||
        appointmentReferenceCount < appointmentItems.length);
    if (appointmentGreen) {
      return "green" as const;
    }
    if (appointmentYellow) {
      return "yellow" as const;
    }
    return "red" as const;
  });

  const hasAppointments = appointmentStatuses.length > 0;
  const allAppointmentsGreen =
    hasAppointments &&
    appointmentStatuses.every((status) => status === "green");
  const allAppointmentsAtLeastYellow =
    hasAppointments &&
    appointmentStatuses.every(
      (status) => status === "green" || status === "yellow",
    );

  const yellowCondition = allAppointmentsAtLeastYellow && !allAppointmentsGreen;
  const greenCondition =
    allAppointmentsGreen &&
    everyAppointmentHasConnections &&
    allReceived &&
    allReferences &&
    hasAppointments;
  const grayCondition =
    type === "medical"
      ? treatment.recordNotApplicable
      : treatment.billNotApplicable;
  return (
    <TooltipWrapper message={grayCondition ? "Not applicable" : message}>
      <div>
        <Icon
          className={cn(
            "h-4 w-4 text-red-500",
            yellowCondition && "text-yellow-500",
            greenCondition && "text-green-500",
            grayCondition && "text-muted-foreground",
          )}
        />
      </div>
    </TooltipWrapper>
  );
}
